import { useEffect, useState } from 'react';
import { goalAPI, predictionAPI } from '../api';
import { Plus, Target, TrendingUp, Trash2, Calendar } from 'lucide-react';
import { toast } from 'sonner';
import { format, differenceInDays } from 'date-fns';

export default function Goals() {
  const [tab, setTab] = useState('goals');
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showPredictionModal, setShowPredictionModal] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const goalsRes = await goalAPI.getAll();
      setGoals(goalsRes.data);
      setLoading(false);
    } catch (error) {
      console.error('Error loading goals:', error);
      toast.error('Failed to load goals');
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this goal?')) return;
    try {
      await goalAPI.delete(id);
      toast.success('Goal deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete goal');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-900 mx-auto"></div>
          <p className="mt-4 text-stone-600">Loading goals...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50 p-6 md:p-12">
      <div className="max-w-6xl mx-auto">
        <div className="flex justify-between items-start mb-8">
          <div>
            <h1 className="text-4xl md:text-5xl font-bold font-heading text-green-900 mb-2">
              Goals & Planning
            </h1>
            <p className="text-stone-500 text-lg">Set financial goals and predict future wealth</p>
          </div>
          <button
            onClick={() => (tab === 'goals' ? setShowAddModal(true) : setShowPredictionModal(true))}
            data-testid="add-goal-btn"
            className="bg-green-900 text-white px-6 py-3 rounded-full font-medium hover:bg-green-800 transition-all shadow-lg hover:shadow-xl active:scale-95"
          >
            <Plus className="inline-block w-5 h-5 mr-2" />
            {tab === 'goals' ? 'Add Goal' : 'New Prediction'}
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-4 mb-6">
          <button
            onClick={() => setTab('goals')}
            data-testid="tab-goals"
            className={`px-6 py-3 rounded-full font-medium transition-all ${
              tab === 'goals' ? 'bg-green-900 text-white shadow-lg' : 'bg-white text-stone-600 hover:bg-stone-100'
            }`}
          >
            Goals ({goals.length})
          </button>
          <button
            onClick={() => setTab('predictions')}
            data-testid="tab-predictions"
            className={`px-6 py-3 rounded-full font-medium transition-all ${
              tab === 'predictions' ? 'bg-green-900 text-white shadow-lg' : 'bg-white text-stone-600 hover:bg-stone-100'
            }`}
          >
            Predictions
          </button>
        </div>

        {tab === 'goals' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="goals-list">
            {goals.length === 0 ? (
              <div className="col-span-2 bg-white rounded-2xl p-12 text-center border border-stone-200">
                <Target className="w-16 h-16 text-stone-300 mx-auto mb-4" />
                <p className="text-stone-500">No goals set yet</p>
              </div>
            ) : (
              goals.map((goal) => {
                const progress = (goal.current_amount / goal.target_amount) * 100;
                const daysLeft = differenceInDays(new Date(goal.target_date), new Date());
                return (
                  <div
                    key={goal.id}
                    className="bg-white rounded-2xl p-6 border border-stone-200 hover:shadow-md transition-all"
                    data-testid={`goal-${goal.id}`}
                  >
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <span
                          className={`px-3 py-1 text-xs font-bold rounded-full ${
                            goal.priority === 'high'
                              ? 'bg-red-100 text-red-900'
                              : goal.priority === 'medium'
                              ? 'bg-yellow-100 text-yellow-900'
                              : 'bg-green-100 text-green-900'
                          }`}
                        >
                          {goal.priority}
                        </span>
                        <h3 className="text-xl font-bold text-green-900 mt-2">{goal.goal_name}</h3>
                        <p className="text-sm text-stone-500 mt-1">{goal.goal_type.replace('_', ' ')}</p>
                      </div>
                      <button
                        onClick={() => handleDelete(goal.id)}
                        data-testid={`delete-goal-${goal.id}`}
                        className="p-2 text-stone-400 hover:text-red-700 hover:bg-red-50 rounded-lg transition-all"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>

                    <div className="mb-4">
                      <div className="flex justify-between items-baseline mb-2">
                        <span className="text-2xl font-bold text-green-900 tabular-nums">
                          {goal.current_amount.toFixed(2)}
                        </span>
                        <span className="text-sm text-stone-500">of {goal.target_amount.toFixed(2)} SAR</span>
                      </div>
                      <div className="w-full bg-stone-200 rounded-full h-3 overflow-hidden">
                        <div
                          className="bg-green-900 h-3 rounded-full transition-all"
                          style={{ width: `${Math.min(progress, 100)}%` }}
                        ></div>
                      </div>
                    </div>

                    <div className="flex justify-between text-sm">
                      <div>
                        <p className="text-stone-500">Progress</p>
                        <p className="font-bold text-green-900 tabular-nums">{progress.toFixed(1)}%</p>
                      </div>
                      <div className="text-right">
                        <p className="text-stone-500">Target Date</p>
                        <p className="font-bold tabular-nums">
                          {format(new Date(goal.target_date), 'MMM d, yyyy')}
                        </p>
                      </div>
                    </div>

                    {daysLeft > 0 && (
                      <p className="text-xs text-stone-500 mt-3">{daysLeft} days remaining</p>
                    )}
                  </div>
                );
              })
            )}
          </div>
        )}

        {tab === 'predictions' && (
          <div className="bg-white rounded-2xl p-6 border border-stone-200">
            <PredictionCalculator />
          </div>
        )}
      </div>

      {showAddModal && (
        <AddGoalModal
          onClose={() => setShowAddModal(false)}
          onSuccess={() => {
            setShowAddModal(false);
            loadData();
          }}
        />
      )}
    </div>
  );
}

function AddGoalModal({ onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    goal_name: '',
    target_amount: '',
    target_date: '',
    goal_type: 'other',
    priority: 'medium',
    monthly_contribution_planned: '',
    current_amount: 0,
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await goalAPI.create({
        ...formData,
        target_amount: parseFloat(formData.target_amount),
        monthly_contribution_planned: formData.monthly_contribution_planned
          ? parseFloat(formData.monthly_contribution_planned)
          : null,
        current_amount: 0,
      });
      toast.success('Goal created successfully!');
      onSuccess();
    } catch (error) {
      console.error('Error creating goal:', error);
      toast.error('Failed to create goal');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div
        className="bg-white rounded-2xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="add-goal-modal"
      >
        <h2 className="text-2xl font-bold font-heading text-green-900 mb-6">Add Goal</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-bold text-stone-700 mb-2">Goal Name</label>
            <input
              type="text"
              value={formData.goal_name}
              onChange={(e) => setFormData({ ...formData, goal_name: e.target.value })}
              required
              data-testid="input-goal-name"
              className="w-full bg-stone-50 border-stone-200 focus:border-green-900 focus:ring-2 focus:ring-green-900/20 rounded-xl h-12 px-4 transition-all"
            />
          </div>
          <div>
            <label className="block text-sm font-bold text-stone-700 mb-2">Goal Type</label>
            <select
              value={formData.goal_type}
              onChange={(e) => setFormData({ ...formData, goal_type: e.target.value })}
              required
              data-testid="input-goal-type"
              className="w-full bg-stone-50 border-stone-200 focus:border-green-900 focus:ring-2 focus:ring-green-900/20 rounded-xl h-12 px-4 transition-all"
            >
              <option value="emergency_fund">Emergency Fund</option>
              <option value="investment">Investment</option>
              <option value="property_purchase">Property Purchase</option>
              <option value="loan_closure">Loan Closure</option>
              <option value="travel">Travel</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-bold text-stone-700 mb-2">Target Amount (SAR)</label>
              <input
                type="number"
                step="0.01"
                value={formData.target_amount}
                onChange={(e) => setFormData({ ...formData, target_amount: e.target.value })}
                required
                data-testid="input-target-amount"
                className="w-full bg-stone-50 border-stone-200 focus:border-green-900 focus:ring-2 focus:ring-green-900/20 rounded-xl h-12 px-4 transition-all"
              />
            </div>
            <div>
              <label className="block text-sm font-bold text-stone-700 mb-2">Target Date</label>
              <input
                type="date"
                value={formData.target_date}
                onChange={(e) => setFormData({ ...formData, target_date: e.target.value })}
                required
                data-testid="input-target-date"
                className="w-full bg-stone-50 border-stone-200 focus:border-green-900 focus:ring-2 focus:ring-green-900/20 rounded-xl h-12 px-4 transition-all"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-bold text-stone-700 mb-2">Priority</label>
            <select
              value={formData.priority}
              onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
              data-testid="input-priority"
              className="w-full bg-stone-50 border-stone-200 focus:border-green-900 focus:ring-2 focus:ring-green-900/20 rounded-xl h-12 px-4 transition-all"
            >
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-bold text-stone-700 mb-2">
              Monthly Contribution (Optional)
            </label>
            <input
              type="number"
              step="0.01"
              value={formData.monthly_contribution_planned}
              onChange={(e) =>
                setFormData({ ...formData, monthly_contribution_planned: e.target.value })
              }
              data-testid="input-monthly-contribution"
              className="w-full bg-stone-50 border-stone-200 focus:border-green-900 focus:ring-2 focus:ring-green-900/20 rounded-xl h-12 px-4 transition-all"
            />
          </div>
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              data-testid="modal-cancel-btn"
              className="flex-1 px-6 py-3 bg-stone-100 text-stone-900 rounded-full font-medium hover:bg-stone-200 transition-all"
            >
              Cancel
            </button>
            <button
              type="submit"
              data-testid="modal-submit-btn"
              className="flex-1 px-6 py-3 bg-green-900 text-white rounded-full font-medium hover:bg-green-800 transition-all shadow-lg hover:shadow-xl active:scale-95"
            >
              Create
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function PredictionCalculator() {
  const [horizonDate, setHorizonDate] = useState('');
  const [predictions, setPredictions] = useState(null);
  const [loading, setLoading] = useState(false);
  const [scenario, setScenario] = useState('base_case');

  const handleCalculate = async () => {
    if (!horizonDate) {
      toast.error('Please select a target date');
      return;
    }

    setLoading(true);
    try {
      const res = await predictionAPI.calculate(horizonDate, scenario);
      setPredictions(res.data);
    } catch (error) {
      console.error('Error calculating prediction:', error);
      toast.error('Failed to calculate prediction');
    }
    setLoading(false);
  };

  return (
    <div data-testid="prediction-calculator">
      <h2 className="text-2xl font-bold font-heading text-green-900 mb-6">Future Wealth Predictor</h2>
      <p className="text-stone-600 mb-6">
        See your projected net worth, assets, and liabilities at any future date based on your recent spending
        patterns and investment growth.
      </p>

      <div className="space-y-4 mb-6">
        <div>
          <label className="block text-sm font-bold text-stone-700 mb-2">Target Date</label>
          <input
            type="date"
            value={horizonDate}
            onChange={(e) => setHorizonDate(e.target.value)}
            min={new Date().toISOString().split('T')[0]}
            data-testid="input-horizon-date"
            className="w-full bg-stone-50 border-stone-200 focus:border-green-900 focus:ring-2 focus:ring-green-900/20 rounded-xl h-12 px-4 transition-all"
          />
        </div>
        <div>
          <label className="block text-sm font-bold text-stone-700 mb-2">Scenario</label>
          <div className="grid grid-cols-3 gap-3">
            <button
              type="button"
              onClick={() => setScenario('optimistic')}
              data-testid="scenario-optimistic"
              className={`px-4 py-3 rounded-xl font-medium transition-all ${
                scenario === 'optimistic'
                  ? 'bg-green-900 text-white shadow-lg'
                  : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
              }`}
            >
              Optimistic
            </button>
            <button
              type="button"
              onClick={() => setScenario('base_case')}
              data-testid="scenario-base"
              className={`px-4 py-3 rounded-xl font-medium transition-all ${
                scenario === 'base_case'
                  ? 'bg-green-900 text-white shadow-lg'
                  : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
              }`}
            >
              Base
            </button>
            <button
              type="button"
              onClick={() => setScenario('pessimistic')}
              data-testid="scenario-pessimistic"
              className={`px-4 py-3 rounded-xl font-medium transition-all ${
                scenario === 'pessimistic'
                  ? 'bg-green-900 text-white shadow-lg'
                  : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
              }`}
            >
              Pessimistic
            </button>
          </div>
          <p className="text-xs text-stone-500 mt-2">
            Optimistic: +10% income, -10% expenses | Pessimistic: -10% income, +10% expenses
          </p>
        </div>
      </div>

      <button
        onClick={handleCalculate}
        disabled={loading}
        data-testid="calculate-prediction-btn"
        className="w-full bg-green-900 text-white px-6 py-3 rounded-full font-medium hover:bg-green-800 transition-all shadow-lg hover:shadow-xl active:scale-95 disabled:opacity-50"
      >
        {loading ? 'Calculating...' : 'Calculate Projection'}
      </button>

      {predictions && (
        <div className="mt-8 space-y-4" data-testid="prediction-results">
          <div className="bg-gradient-to-r from-green-50 to-lime-50 rounded-2xl p-6 border border-lime-200">
            <h3 className="text-sm font-bold text-stone-500 uppercase tracking-widest mb-3">
              Projected Net Worth
            </h3>
            <div className="text-5xl font-bold text-green-900 tabular-nums">
              {predictions.projected_net_worth.toFixed(2)}
              <span className="text-2xl ml-2 text-stone-500">SAR</span>
            </div>
            <p className="text-sm text-stone-600 mt-2">
              In {predictions.months_ahead} months ({scenario.replace('_', ' ')} scenario)
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="bg-white rounded-2xl p-4 border border-stone-200">
              <p className="text-xs font-bold text-stone-500 uppercase tracking-widest mb-2">Assets</p>
              <p className="text-2xl font-bold text-green-900 tabular-nums">
                {predictions.projected_total_assets.toFixed(2)}
              </p>
            </div>
            <div className="bg-white rounded-2xl p-4 border border-stone-200">
              <p className="text-xs font-bold text-stone-500 uppercase tracking-widest mb-2">Liabilities</p>
              <p className="text-2xl font-bold text-red-700 tabular-nums">
                {predictions.projected_total_liabilities.toFixed(2)}
              </p>
            </div>
            <div className="bg-white rounded-2xl p-4 border border-stone-200">
              <p className="text-xs font-bold text-stone-500 uppercase tracking-widest mb-2">Cash Balance</p>
              <p className="text-2xl font-bold text-green-900 tabular-nums">
                {predictions.projected_cash_balance.toFixed(2)}
              </p>
            </div>
            <div className="bg-white rounded-2xl p-4 border border-stone-200">
              <p className="text-xs font-bold text-stone-500 uppercase tracking-widest mb-2">
                Monthly Flow
              </p>
              <p className="text-2xl font-bold text-green-900 tabular-nums">
                {predictions.assumptions.net_monthly_flow.toFixed(2)}
              </p>
            </div>
          </div>

          <div className="bg-stone-100 rounded-2xl p-4">
            <p className="text-xs font-bold text-stone-500 uppercase tracking-widest mb-3">Assumptions</p>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <span className="text-stone-600">Avg Monthly Income:</span>
                <span className="font-bold ml-2 tabular-nums">
                  {predictions.assumptions.avg_monthly_income.toFixed(2)} SAR
                </span>
              </div>
              <div>
                <span className="text-stone-600">Avg Monthly Expense:</span>
                <span className="font-bold ml-2 tabular-nums">
                  {predictions.assumptions.avg_monthly_expense.toFixed(2)} SAR
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
